import React from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';

// Regexes are tested in order, the first matching one is used
const regexes = [
  {
    regex: /^\/activity\/article\//,
    page: null,
  },
  {
    regex: /^\/science-fair\/article\//,
    page: null,
  },
  {
    regex: /^\/games\/play($|[/?#])/,
    page: null,
  },
  {
    regex: /^\/worksheet\/article\//,
    page: null,
  },
  {
    regex: /^\/game\//,
    page: null,
  },
  {
    regex: /^\/lesson-plan\//,
    page: null,
  },
  {
    regex: /^\/learn\//,
    page: null,
  },
  {
    regex: /^\/open-interactive\//,
    page: null,
  },
  {
    regex: /^\/(resources|worksheets|workbooks|songs|stories|games|lesson-plans|exercises|guided-lessons|activity|science-fair)\//,
    page: '/[...search]',
  },
];

const getPage = href => {
  for (let { regex, page } of regexes) {
    if (regex.test(href)) {
      return page;
    }
  }
  return null;
};

const getComponent = (children, componentProps) => {
  // If a component that accepts `href` is passed in, use that
  if (React.Children.count(children) === 1) {
    let component;
    React.Children.forEach(children, child => {
      if (!React.isValidElement(child) || !child.type) {
        return;
      }

      // Either an <a> tag or a custom component that accepts an `href` prop
      if (
        child.type === 'a' ||
        (child.type.propTypes && child.type.propTypes.href)
      ) {
        component = React.cloneElement(child, componentProps);
      }
    });

    if (component) {
      return component;
    }
  }

  // Otherwise, wrap the children with an <a> component
  return <a {...componentProps}>{children}</a>;
};

const AppLink = ({ href, children, ...otherProps }) => {
  const page = getPage(href);

  // The props passed to the underlying `<a>` component
  const componentProps = { ...otherProps };
  if (!page) {
    // Not a nextjs page, make sure the component has an href
    componentProps.href = href;
  }
  const component = getComponent(children, componentProps);

  // Not a nextjs page, don't need to support client-side routing
  if (!page) {
    return component;
  }

  // NextJS links needs to wrap in the next/link component for client-side routing
  return (
    <Link href={page} as={href} passHref={true}>
      {component}
    </Link>
  );
};

AppLink.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.node,
};

export default AppLink;

export async function pushAppRoute(router, href) {
  const page = getPage(href);

  // Page is not nextjs, do a full page load
  if (!page) {
    window.location.href = href;
    return;
  }

  // NextJS pages can do client-side routing
  return await router.push('/[...search]', href);
}
